import { apiClient } from './api';
import type { Certificate, CertificateVerification, PublicCertificateVerification } from '../types/certificate';

// Public API base URL (no auth token needed)
const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

export interface CertificateListItem {
  id: string
  token: string
  enrollment_id: string
  issued_at: string
  expires_at?: string
  status: string
  pdf_url: string
  user_name: string
  user_email: string
  course_name: string
  revoked_at?: string
}

export interface CertificateListResponse {
  data: CertificateListItem[]
  meta: {
    total: number
    page: number
    limit: number
  }
}

export const certificateService = {
  // Generate certificate
  generateCertificate: async (enrollmentId: string): Promise<Certificate> => {
    const response = await apiClient.post<Certificate>(`/certificates/generate/${enrollmentId}`);
    return response.data as Certificate;
  },

  // Get certificate by ID
  getCertificate: async (id: string): Promise<Certificate> => {
    const response = await apiClient.get<Certificate>(`/certificates/${id}`);
    return response.data as Certificate;
  },

  // Verify certificate by token
  verifyCertificate: async (token: string): Promise<CertificateVerification> => {
    const response = await apiClient.get<CertificateVerification>(`/certificates/verify/${token}`);
    return response.data as CertificateVerification;
  },

  // Get user certificates
  getUserCertificates: async (userId: string): Promise<Certificate[]> => {
    const response = await apiClient.get<Certificate[]>(`/certificates/user/${userId}`);
    return response.data as Certificate[];
  },

  // Get my certificates (current user) - uses getUserCertificates with current user ID
  getMyCertificates: async (userId: string): Promise<Certificate[]> => {
    const response = await apiClient.get<Certificate[]>(`/certificates/user/${userId}`);
    return response.data as Certificate[];
  },

  // Admin: list all certificates
  listAll: async (params?: { page?: number; limit?: number; search?: string }): Promise<CertificateListResponse> => {
    const response = await apiClient.get<CertificateListResponse>('/admin/certificates', { params });
    return response.data as CertificateListResponse;
  },

  // Admin: revoke certificate with reason
  adminRevoke: async (id: string, reason?: string): Promise<void> => {
    await apiClient.post(`/certificates/${id}/revoke`, { reason });
  },

  // Public certificate verification (no auth required)
  publicVerify: async (token: string): Promise<PublicCertificateVerification> => {
    const response = await fetch(`${API_BASE}/verify?token=${encodeURIComponent(token)}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Certificate verification failed');
    }

    return data.data as PublicCertificateVerification;
  },
};
